import { useGetBusinessInformationDetails } from '@src/hooks/queries/onboarding';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useBusinessInformationForm from './useBusinessInformationForm';
import useBusinessDocumentationForm from './useBusinessDocumentationForm';
import { getControlPersonInfoInitialValues } from '../utils/getControlPersonInitialValues';
import useDebounce from '@src/hooks/utils/useDebounce';
import { handleValidateControlPersonData } from '../utils/handleControlPersonData';

interface IUseOnboardingVerificationCheckOptions {
  isBusinessComplete: boolean | undefined;
}

const useOnboardingVerificationCheck = ({
  isBusinessComplete
}: IUseOnboardingVerificationCheckOptions) => {
  const [isBeneficialOwnerSet, setIsBeneficialOwnerSet] = useState(false);
  const [isBeneficialOwnersVerified, setIsBeneficialOwnersVerified] = useState(false);

  const { data: businessInformationDetails, isLoading: businessInformationDetailsLoading } =
    useGetBusinessInformationDetails({ enabled: !!isBusinessComplete });

  const {
    initialValues: businessInformationInitialValues,
    validationSchema: businessInformationValidationSchema,
    businessIndustriesLoading,
    businessTypesLoading
  } = useBusinessInformationForm({
    businessInformationDetails
  });

  const {
    initialValues: businessDocumentationInitialValues,
    validationSchema: businessDocumentationValidationSchema
  } = useBusinessDocumentationForm({
    businessInformationDetails
  });

  const beneficialOwnersInitialValues = useMemo(
    () => getControlPersonInfoInitialValues(businessInformationDetails || null),
    [businessInformationDetails]
  );

  const debouncedBusinessInformationDetailsLoading = useDebounce(businessInformationDetailsLoading);

  const isBusinessInformationVerified = useMemo(() => {
    try {
      if (
        businessIndustriesLoading ||
        businessTypesLoading ||
        businessInformationDetailsLoading ||
        !businessInformationDetails
      )
        return false;

      return businessInformationValidationSchema.isValidSync(businessInformationInitialValues);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    businessInformationInitialValues,
    businessInformationValidationSchema,
    businessIndustriesLoading,
    businessTypesLoading,
    businessInformationDetailsLoading,
    businessInformationDetails
  ]);

  const isBusinessDocumentationVerified = useMemo(() => {
    try {
      if (businessInformationDetailsLoading || !businessInformationDetails) return false;

      return businessDocumentationValidationSchema.isValidSync(businessDocumentationInitialValues);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    businessDocumentationInitialValues,
    businessDocumentationValidationSchema,
    businessInformationDetailsLoading,
    businessInformationDetails
  ]);

  const handleValidateBusinessOwner = useCallback(async () => {
    try {
      if (businessInformationDetailsLoading || !businessInformationDetails)
        // if (businessInformationDetailsLoading || !businessInformationDetails || isBeneficialOwnerSet)
        return;

      const { fullErrorObj } = await handleValidateControlPersonData(beneficialOwnersInitialValues);

      setIsBeneficialOwnersVerified(!fullErrorObj);
      setIsBeneficialOwnerSet(true);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    beneficialOwnersInitialValues,
    businessInformationDetails,
    businessInformationDetailsLoading,
    isBeneficialOwnerSet
  ]);

  useEffect(() => {
    handleValidateBusinessOwner();
  }, [handleValidateBusinessOwner]);

  const isOnboardingValidated =
    isBeneficialOwnersVerified && isBusinessDocumentationVerified && isBusinessInformationVerified;

  const debouncedIsOnboardingValidated = useDebounce(isOnboardingValidated);

  return {
    isOnboardingValidated: debouncedIsOnboardingValidated,
    businessInformationDetailsLoading:
      debouncedBusinessInformationDetailsLoading || businessInformationDetailsLoading
  };
};

export default useOnboardingVerificationCheck;
